import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Zap, Calendar } from "lucide-react";
import RouteMap from "../components/dashboard/RouteMap";
import LazyLoadRoute from "../components/dashboard/LazyLoadRoute";
import SearchableSelect from "../components/SearchableSelect";

// Custom Hooks
import useAddressSearch from "../hooks/useAddressSearch";
import useBookingForm from "../hooks/useBookingForm";
import useBookingRecommendations from "../hooks/useBookingRecommendations";
import usePreviousRequests from "../hooks/usePreviousRequests";

// Custom Components
import DurationInput from "../components/bookings/form/DurationInput";
import BufferInput from "../components/bookings/form/BufferInput";
import PaginationControls from "../components/bookings/shared/PaginationControls";
import RecommendationCardHeader from "../components/bookings/recommendations/RecommendationCardHeader";
import RouteDetails from "../components/bookings/recommendations/RouteDetails";

/**
 * NoRecommendationsState Component
 * Shows when there are no recommendations for a booking request
 */
function NoRecommendationsState() {
  return (
    <Box
      p={8}
      borderRadius="xl"
      bg="yellow.50"
      border="2px solid"
      borderColor="yellow.200"
      textAlign="center"
    >
      <Calendar size={48} color="#D69E2E" style={{ margin: "0 auto 16px" }} />
      <Heading size="md" color="yellow.900" mb={3}>
        No Available Time Slots Found
      </Heading>
      <Text color="yellow.800" mb={4}>
        We couldn't find any schedule openings that fit this request.
      </Text>
    </Box>
  );
}

export default function Bookings({ user }) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams();
  const productDropdownRef = useRef(null);
  const addressDropdownRef = useRef(null);
  const workerDropdownRef = useRef(null);

  const [loading, setLoading] = useState(true);

  // Dropdown states
  const [products, setProducts] = useState([]);

  // Keyboard navigation states
  const [highlightedAddressIndex, setHighlightedAddressIndex] = useState(-1);

  // Worker filter state
  const [workers, setWorkers] = useState([]);
  const [workerSearch, setWorkerSearch] = useState("");
  const [showWorkerDropdown, setShowWorkerDropdown] = useState(false);

  // Determine view from URL
  const view = requestId ? "viewing" : location.pathname === "/schedule/previous" ? "previous" : "new";
  const viewingPreviousRequest = !!requestId;
  const providerLabel = user?.connected_provider === "service_m8" ? "ServiceM8" : "Jobber";

  // Custom Hooks
  const addressSearch = useAddressSearch();
  const bookingForm = useBookingForm({
    onSuccess: (data) => {
      recommendations.setBookingRequest(data.booking_request);
      recommendations.setRecommendations(data.recommendations);
    }
  });
  const recommendations = useBookingRecommendations();
  const previousRequests = usePreviousRequests();

  useEffect(() => {
    loadInitialData();
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        bookingForm.setShowProductDropdown(false);
        bookingForm.setProductSearch("");
      }
      if (addressDropdownRef.current && !addressDropdownRef.current.contains(event.target)) {
        addressSearch.closeDropdown();
      }
      if (workerDropdownRef.current && !workerDropdownRef.current.contains(event.target)) {
        setShowWorkerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      // Fetch products and workers in parallel
      const [productsResponse, workersResponse] = await Promise.all([
        fetch("/api/service_types", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
        fetch("/api/workers", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
      ]);

      const productsData = await productsResponse.json();
      const workersData = await workersResponse.json();

      if (productsData.success) {
        setProducts(productsData.products);
      }

      if (workersData.success) {
        setWorkers(workersData.workers);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast({
        title: "Error loading data",
        description: "Failed to load products. Please refresh the page.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Load request details when clicking on a previous request
  const loadRequestDetails = async (id) => {
    // Navigate to the request URL
    navigate(`/schedule/previous/${id}`);
  };

  // Load request data when requestId changes from URL
  useEffect(() => {
    if (requestId) {
      const fetchRequestData = async () => {
        try {
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
          const response = await fetch(`/api/last_minute_bookings/${requestId}`, {
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken,
            },
          });

          const data = await response.json();
          if (data.success) {
            const request = data.booking_request;

            // Populate form with request data
            bookingForm.populateForm(request);

            // Set recommendations
            recommendations.setBookingRequest(request);
            recommendations.setRecommendations(data.recommendations);

            // Reset pagination and filters for clean display
            recommendations.setCurrentPage(1);
            recommendations.setSelectedWorkerIds([]);
          } else {
            toast({
              title: "Error",
              description: data.error || "Failed to load request details",
              status: "error",
              duration: 3000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error("Error loading request details:", error);
          toast({
            title: "Error",
            description: "Failed to load request details",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      };

      fetchRequestData();
    }
  }, [requestId]);

  // Load previous requests when navigating to /bookings/previous
  useEffect(() => {
    if (view === "previous" && previousRequests.previousRequests.length === 0) {
      previousRequests.fetchPreviousRequests();
    }
  }, [view]);

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(bookingForm.productSearch.toLowerCase()) ||
    product.description?.toLowerCase().includes(bookingForm.productSearch.toLowerCase())
  );

  const hasAddressResults = addressSearch.clientAddresses.length > 0 || addressSearch.awsAddresses.length > 0;
  const showNoResults = addressSearch.showAddressDropdown && addressSearch.addressSearch.length >= 2 && !addressSearch.searchingAddresses && !hasAddressResults;

  // Combined address list for keyboard navigation
  const allAddresses = [...addressSearch.clientAddresses, ...addressSearch.awsAddresses];

  // Keyboard navigation handlers
  const handleProductKeyDown = (e) => {
    if (!bookingForm.showProductDropdown || filteredProducts.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        bookingForm.setHighlightedProductIndex(prev =>
          prev < filteredProducts.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        bookingForm.setHighlightedProductIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (bookingForm.highlightedProductIndex >= 0 && bookingForm.highlightedProductIndex < filteredProducts.length) {
          bookingForm.handleProductSelect(filteredProducts[bookingForm.highlightedProductIndex]);
        }
        break;
      case "Escape":
        bookingForm.setShowProductDropdown(false);
        bookingForm.setProductSearch("");
        break;
    }
  };

  const handleAddressKeyDown = (e) => {
    if (!addressSearch.showAddressDropdown || allAddresses.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedAddressIndex(prev =>
          prev < allAddresses.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedAddressIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedAddressIndex >= 0 && highlightedAddressIndex < allAddresses.length) {
          const selectedAddr = allAddresses[highlightedAddressIndex];
          if (highlightedAddressIndex < addressSearch.clientAddresses.length) {
            bookingForm.handleClientAddressSelect(selectedAddr, addressSearch.closeDropdown);
          } else {
            bookingForm.handleAwsAddressSelect(selectedAddr, addressSearch.closeDropdown);
          }
        }
        break;
      case "Escape":
        addressSearch.closeDropdown();
        break;
    }
  };

  // Reset highlighted index when dropdown opens/closes or results change
  useEffect(() => {
    bookingForm.setHighlightedProductIndex(-1);
  }, [bookingForm.showProductDropdown, bookingForm.productSearch]);

  useEffect(() => {
    setHighlightedAddressIndex(-1);
  }, [addressSearch.showAddressDropdown, addressSearch.clientAddresses, addressSearch.awsAddresses]);

  if (loading) {
    return (
      <Flex minH="calc(100vh - 100px)" align="center" justify="center">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Flex>
    );
  }

  return (
    <Box maxW="1800px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      {/* Header */}
      <Box mb={{ base: 6, md: 8 }}>
        <Flex align={{ base: "stretch", sm: "center" }} justify="space-between" mb={2} gap={3} direction={{ base: "column", sm: "row" }}>
          <Flex align="center" gap={2}>
            <Zap size={32} color="#3182ce" />
            <Heading size={{ base: "lg", md: "xl" }} color="gray.800">
              Smart Schedule Finder
            </Heading>
          </Flex>
          <HStack spacing={2} alignSelf={{ base: "stretch", sm: "auto" }} sx={{ "& > button": { width: { base: "100%", sm: "auto" }, minH: "44px" } }}>
            {viewingPreviousRequest ? (
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={() => {
                  navigate("/schedule/previous");
                  recommendations.resetRecommendations();
                  bookingForm.resetForm();
                }}
              >
                ← Back to Previous Requests
              </Button>
            ) : view === "new" ? (
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={() => {
                  navigate("/schedule/previous");
                  if (previousRequests.previousRequests.length === 0) {
                    previousRequests.fetchPreviousRequests();
                  }
                }}
              >
                Previous Requests
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={() => {
                  navigate("/schedule");
                  recommendations.resetRecommendations();
                }}
              >
                New Request
              </Button>
            )}
          </HStack>
        </Flex>
        <Text color="gray.600" fontSize="sm">
          {viewingPreviousRequest
            ? "Viewing a previous booking request. Form fields are read-only."
            : view === "new"
            ? "Find optimal available time slots for your client's needs."
            : "View your previous booking requests and their recommendations."}
        </Text>
      </Box>

      {/* Two Column Layout */}
      {view === "new" || view === "viewing" ? (
        <Flex gap={6} align="flex-start" direction={{ base: "column", xl: "row" }}>
          {/* Left Column - Form */}
          <Box flex={{ base: "1 1 auto", xl: "0 0 500px" }} minW={0} w="full">
          <Box
            p={{ base: 4, sm: 6, md: 8 }}
            borderRadius="xl"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
          >
            <Heading size="md" mb={2}>
              {viewingPreviousRequest ? "Viewing Previous Request" : "New Request"}
            </Heading>
            <Text color="gray.600" fontSize="sm" mb={6}>
              {viewingPreviousRequest
                ? "Review the details and recommendations from this previous request"
                : "Fill out the details below to find available time slots"}
            </Text>

        <form onSubmit={(e) => bookingForm.handleSubmit(e, recommendations.resetRecommendations)}>
          <VStack spacing={6} align="stretch">
            {/* Client Address */}
            <FormControl isRequired position="relative" ref={addressDropdownRef}>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Client Address
              </FormLabel>
              <Input
                value={addressSearch.showAddressDropdown ? addressSearch.addressSearch : (bookingForm.selectedAddress?.full_address || bookingForm.selectedAddress?.formatted_address || bookingForm.formData.address || "")}
                onChange={(e) => {
                  addressSearch.setAddressSearch(e.target.value);
                  addressSearch.setShowAddressDropdown(true);
                }}
                onFocus={() => {
                  if (!viewingPreviousRequest) {
                    addressSearch.setAddressSearch("");
                    addressSearch.setShowAddressDropdown(true);
                  }
                }}
                onKeyDown={handleAddressKeyDown}
                placeholder="Search for an address..."
                size="lg"
                borderWidth="2px"
                isReadOnly={viewingPreviousRequest}
                bg={viewingPreviousRequest ? "gray.50" : "white"}
              />
              {addressSearch.showAddressDropdown && !viewingPreviousRequest && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  mt={1}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  boxShadow="lg"
                  maxH="400px"
                  overflowY="auto"
                  zIndex={1000}
                >
                  {addressSearch.searchingAddresses ? (
                    <Box px={4} py={3} textAlign="center">
                      <Spinner size="sm" color="blue.500" />
                      <Text fontSize="sm" color="gray.500" mt={2}>
                        Searching...
                      </Text>
                    </Box>
                  ) : (
                    <>
                      {/* Client Addresses Section */}
                      {addressSearch.clientAddresses.length > 0 && (
                        <>
                          <Box px={4} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="bold" color="gray.600" textTransform="uppercase">
                              Your Clients
                            </Text>
                          </Box>
                          {addressSearch.clientAddresses.map((address, index) => (
                            <Box
                              key={address.id}
                              px={4}
                              py={3}
                              cursor="pointer"
                              _hover={{ bg: "blue.50" }}
                              bg={
                                highlightedAddressIndex === index
                                  ? "blue.100"
                                  : address.id === bookingForm.formData.propertyId
                                  ? "blue.50"
                                  : "white"
                              }
                              onClick={() => bookingForm.handleClientAddressSelect(address, addressSearch.closeDropdown)}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                            >
                              <Text fontSize="sm" fontWeight="medium">
                                {address.client_name}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {address.full_address}
                              </Text>
                            </Box>
                          ))}
                        </>
                      )}

                      {/* AWS Location Addresses Section */}
                      {addressSearch.awsAddresses.length > 0 && (
                        <>
                          <Box px={4} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="bold" color="gray.600" textTransform="uppercase">
                              Other Addresses
                            </Text>
                          </Box>
                          {addressSearch.awsAddresses.map((address, index) => {
                            const globalIndex = addressSearch.clientAddresses.length + index;
                            return (
                              <Box
                                key={`aws-${index}`}
                                px={4}
                                py={3}
                                cursor="pointer"
                                _hover={{ bg: "blue.50" }}
                                bg={
                                  highlightedAddressIndex === globalIndex
                                    ? "blue.100"
                                    : address.place_id === bookingForm.formData.awsPlaceId
                                    ? "blue.50"
                                    : "white"
                                }
                                onClick={() => bookingForm.handleAwsAddressSelect(address, addressSearch.closeDropdown)}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                              >
                                <Text fontSize="sm">
                                  {address.formatted_address}
                                </Text>
                              </Box>
                            );
                          })}
                        </>
                      )}

                      {/* No Results */}
                      {showNoResults && (
                        <Box px={4} py={3}>
                          <Text fontSize="sm" color="gray.500">
                            {addressSearch.addressSearch.length < 2
                              ? "Type at least 2 characters to search"
                              : "No addresses found"}
                          </Text>
                        </Box>
                      )}

                      {/* Prompt to start typing */}
                      {!addressSearch.searchingAddresses && addressSearch.addressSearch.length < 2 && (
                        <Box px={4} py={3}>
                          <Text fontSize="sm" color="gray.500">
                            Start typing to search addresses...
                          </Text>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </FormControl>

            {/* Product/Service */}
            <FormControl position="relative" ref={productDropdownRef}>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Product/Service
              </FormLabel>
              <Input
                value={bookingForm.showProductDropdown ? bookingForm.productSearch : (bookingForm.selectedProduct?.name || "")}
                onChange={(e) => {
                  bookingForm.setProductSearch(e.target.value);
                  bookingForm.setShowProductDropdown(true);
                }}
                onFocus={() => {
                  if (!viewingPreviousRequest) {
                    bookingForm.setProductSearch("");
                    bookingForm.setShowProductDropdown(true);
                  }
                }}
                onKeyDown={handleProductKeyDown}
                placeholder="Search for a product or service..."
                size="lg"
                borderWidth="2px"
                isReadOnly={viewingPreviousRequest}
                bg={viewingPreviousRequest ? "gray.50" : "white"}
              />
              {bookingForm.showProductDropdown && !viewingPreviousRequest && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  mt={1}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  boxShadow="lg"
                  maxH="300px"
                  overflowY="auto"
                  zIndex={1000}
                >
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product, index) => (
                      <Box
                        key={product.id}
                        px={4}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: "blue.50" }}
                        bg={
                          bookingForm.highlightedProductIndex === index
                            ? "blue.100"
                            : product.id === bookingForm.formData.productId
                            ? "blue.50"
                            : "white"
                        }
                        onClick={() => bookingForm.handleProductSelect(product)}
                      >
                        <Text fontSize="sm" fontWeight="medium">
                          {product.name}
                        </Text>
                        {product.duration_minutes && (
                          <Text fontSize="xs" color="gray.600">
                            {Math.floor(product.duration_minutes / 60)}h {product.duration_minutes % 60}m
                          </Text>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box px={4} py={2}>
                      <Text fontSize="sm" color="gray.500">
                        No products/services found
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>

            {/* Duration */}
            <DurationInput
              hours={bookingForm.formData.durationHours}
              minutes={bookingForm.formData.durationMinutes}
              onChange={(field, value) => {
                bookingForm.updateFormField(field === 'hours' ? 'durationHours' : 'durationMinutes', value);
              }}
              onAdjust={bookingForm.adjustDuration}
              isReadOnly={viewingPreviousRequest}
            />

            {/* Buffer */}
            <BufferInput
              value={bookingForm.formData.bufferMinutes}
              onChange={(value) => bookingForm.updateFormField('bufferMinutes', value)}
              isReadOnly={viewingPreviousRequest}
            />

            {/* After Date Filter (Optional) */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Only Show Slots After (Optional)
              </FormLabel>
              <Input
                type="date"
                value={bookingForm.formData.afterDate || ''}
                onChange={(e) => bookingForm.updateFormField('afterDate', e.target.value)}
                size="lg"
                borderWidth="2px"
                isReadOnly={viewingPreviousRequest}
                bg={viewingPreviousRequest ? "gray.50" : "white"}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Leave blank to see all available slots
              </Text>
            </FormControl>

            {/* Submit Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              width="100%"
              isLoading={bookingForm.submitting}
              isDisabled={viewingPreviousRequest || !bookingForm.isFormSubmittable()}
              mt={2}
            >
              {viewingPreviousRequest ? "Viewing Previous Request" : "Show Recommendations"}
            </Button>
          </VStack>
        </form>
          </Box>
        </Box>

        {/* Right Column - Recommendations */}
        <Box flex="1" minW={0} w="full">
          {recommendations.recommendations.length === 0 && recommendations.bookingRequest ? (
            <NoRecommendationsState />
          ) : recommendations.recommendations.length === 0 ? (
            <Box
              p={8}
              borderRadius="xl"
              bg="gray.50"
              border="2px dashed"
              borderColor="gray.300"
              textAlign="center"
            >
              <Text fontSize="lg" color="gray.500" fontWeight="medium">
                No recommendations yet
              </Text>
              <Text fontSize="sm" color="gray.400" mt={2}>
                Fill out the form and click "Show Recommendations" to see available time slots
              </Text>
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              {/* Recommendations Header */}
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius="xl"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <Heading size="md" mb={3}>
                  Available Time Slots
                </Heading>

                {/* Worker Filter */}
                <FormControl mb={3}>
                  <FormLabel fontSize="sm" mb={2}>
                    Filter by Worker
                  </FormLabel>
                  <SearchableSelect
                    options={workers || []}
                    getOptionLabel={(worker) => worker.name}
                    getOptionValue={(worker) => worker.id}
                    selectedValues={recommendations.selectedWorkerIds}
                    onChange={(newValues) => {
                      recommendations.setSelectedWorkerIds(newValues);
                      recommendations.setCurrentPage(1);
                    }}
                    placeholder="Search workers..."
                    multiSelect={true}
                    size="sm"
                  />
                  {recommendations.selectedWorkerIds.length > 0 && (
                    <Flex gap={2} mt={2} flexWrap="wrap">
                      {recommendations.selectedWorkerIds.map((workerId) => {
                        const worker = (workers || []).find((w) => w.id === workerId);
                        return worker ? (
                          <Box
                            key={workerId}
                            px={2}
                            py={1}
                            bg="blue.100"
                            borderRadius="md"
                            fontSize="xs"
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Text>{worker.name}</Text>
                            <Box
                              as="button"
                              ml={1}
                              color="blue.700"
                              fontWeight="bold"
                              onClick={() => {
                                recommendations.setSelectedWorkerIds(recommendations.selectedWorkerIds.filter((id) => id !== workerId));
                                recommendations.setCurrentPage(1);
                              }}
                            >
                              ×
                            </Box>
                          </Box>
                        ) : null;
                      })}
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => {
                          recommendations.setSelectedWorkerIds([]);
                          recommendations.setCurrentPage(1);
                        }}
                      >
                        Clear all
                      </Button>
                    </Flex>
                  )}
                </FormControl>

                {/* Date & Time Filters */}
                <Flex gap={3} mb={3} flexWrap="wrap">
                  {/* After Date Filter */}
                  <FormControl flex="1" minW={{ base: "100%", sm: "200px" }}>
                    <FormLabel fontSize="xs" mb={1}>
                      After Date
                    </FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={recommendations.filterAfterDate || ''}
                      onChange={(e) => {
                        recommendations.setFilterAfterDate(e.target.value);
                        recommendations.setCurrentPage(1);
                      }}
                    />
                  </FormControl>

                  {/* After Hour Filter */}
                  <FormControl flex="1" minW={{ base: "100%", sm: "150px" }}>
                    <FormLabel fontSize="xs" mb={1}>
                      After Hour
                    </FormLabel>
                    <Input
                      type="number"
                      size="sm"
                      min="0"
                      max="23"
                      placeholder="0-23"
                      value={recommendations.filterAfterHour || ''}
                      onChange={(e) => {
                        recommendations.setFilterAfterHour(e.target.value);
                        recommendations.setCurrentPage(1);
                      }}
                    />
                  </FormControl>

                  {/* Before Hour Filter */}
                  <FormControl flex="1" minW={{ base: "100%", sm: "150px" }}>
                    <FormLabel fontSize="xs" mb={1}>
                      Before Hour
                    </FormLabel>
                    <Input
                      type="number"
                      size="sm"
                      min="0"
                      max="23"
                      placeholder="0-23"
                      value={recommendations.filterBeforeHour || ''}
                      onChange={(e) => {
                        recommendations.setFilterBeforeHour(e.target.value);
                        recommendations.setCurrentPage(1);
                      }}
                    />
                  </FormControl>
                </Flex>

                {(recommendations.filterAfterDate || recommendations.filterAfterHour || recommendations.filterBeforeHour) && (
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    mb={3}
                    onClick={() => {
                      recommendations.setFilterAfterDate('');
                      recommendations.setFilterAfterHour('');
                      recommendations.setFilterBeforeHour('');
                      recommendations.setCurrentPage(1);
                    }}
                  >
                    Clear date/time filters
                  </Button>
                )}

                <Text fontSize="sm" color="gray.600">
                  {(() => {
                    const { filtered } = recommendations.getDisplayedRecommendations();
                    return `Showing ${filtered.length} recommendation${filtered.length !== 1 ? 's' : ''} • Sorted by soonest`;
                  })()}
                </Text>
              </Box>

              {/* Recommendation Cards */}
              {(() => {
                const { paginated, filtered, totalPages } = recommendations.getDisplayedRecommendations();

                // No recommendations state
                if (recommendations.bookingRequest && paginated.length === 0) {
                  return <NoRecommendationsState />;
                }

                return (
                  <>
                    {paginated.map((rec, index) => {
                // Get route data for this recommendation (if loaded)
                const routeData = recommendations.getRouteData(rec.id);
                const isLoadingRoute = recommendations.isRouteLoading(rec.id);

                return (
                  <LazyLoadRoute
                    key={rec.id}
                    recommendationId={rec.id}
                    onVisible={recommendations.fetchRouteForRecommendation}
                  >
                    <Box
                      p={{ base: 4, md: 6 }}
                      borderRadius="xl"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      boxShadow="sm"
                      _hover={{ boxShadow: "md", borderColor: "blue.300" }}
                      transition="all 0.2s"
                    >
                      {/* Recommendation Card Header */}
                      <RecommendationCardHeader
                        rank={rec.rank}
                        isBestMatch={index === 0}
                        worker={rec.worker}
                        proposedStartTime={rec.proposed_start_time}
                        durationMinutes={recommendations.bookingRequest.duration_minutes}
                        scheduleOpeningDuration={rec.schedule_opening.duration_minutes}
                        bufferMinutes={recommendations.bookingRequest.buffer_minutes}
                        routeData={routeData}
                        estimatedDriveTimeMinutes={rec.estimated_drive_time_minutes}
                        isLoadingRoute={isLoadingRoute}
                      />

                      {/* Bottom Row: Route Details and Map side by side */}
                      <Flex gap={4} mb={4} align="flex-start" direction={{ base: "column", lg: "row" }}>
                        {/* Route Details (Left) */}
                        <RouteDetails pointA={rec.point_a} pointC={rec.point_c} />

                        {/* Route Map (Right) */}
                        {routeData && (
                          <Box flex="1" minW={0} w="full">
                            <Text fontSize="sm" color="gray.500" mb={2} fontWeight="medium">
                              Route Map
                            </Text>
                            <RouteMap routes={routeData} height="300px" />
                          </Box>
                        )}
                      </Flex>

                      {/* Instructions Box */}
                      <Box
                        mt={4}
                        p={4}
                        borderRadius="md"
                        bg="blue.50"
                        border="1px solid"
                        borderColor="blue.200"
                      >
                        <Text fontSize="sm" fontWeight="semibold" color="blue.900" mb={2}>
                          To book this time slot in {providerLabel}:
                        </Text>
                        <VStack align="stretch" spacing={2} fontSize="sm" color="blue.800">
                          <Text>
                            <strong>1.</strong> Create or update the client for{" "}
                            <strong>{recommendations.bookingRequest?.address}</strong>
                          </Text>
                          <Text>
                            <strong>2.</strong> Add a property at the address above
                          </Text>
                          <Text>
                            <strong>3.</strong> Create a new job with the requested service
                          </Text>
                          <Text>
                            <strong>4.</strong> Schedule a visit for{" "}
                            <strong>
                              {new Date(rec.proposed_start_time).toLocaleString("en-US", {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </strong>{" "}
                            ({rec.schedule_opening.duration_minutes} min duration)
                          </Text>
                          <Text>
                            <strong>5.</strong> Assign the visit to <strong>{rec.worker.name}</strong>
                          </Text>
                        </VStack>
                        <Text fontSize="xs" color="blue.700" mt={3} fontStyle="italic">
                          Note: This time slot fits between existing jobs. Timing is important to
                          maintain the schedule.
                        </Text>
                      </Box>
                    </Box>
                  </LazyLoadRoute>
                );
              })}

                    {/* Pagination Controls */}
                    <PaginationControls
                      currentPage={recommendations.currentPage}
                      totalPages={totalPages}
                      onPageChange={recommendations.setCurrentPage}
                    />
                  </>
                );
              })()}
            </VStack>
          )}
        </Box>
      </Flex>
      ) : (
        /* Previous Requests View */
        <Box>
          {previousRequests.loadingPrevious ? (
            <Flex justify="center" align="center" minH="400px">
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" />
                <Text color="gray.600">Loading previous requests...</Text>
              </VStack>
            </Flex>
          ) : previousRequests.previousRequests.length === 0 ? (
            <Box
              p={12}
              borderRadius="xl"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="sm"
              textAlign="center"
            >
              <Text fontSize="lg" color="gray.600" mb={2}>
                No previous requests found
              </Text>
              <Text fontSize="sm" color="gray.500">
                Create your first last-minute booking request to see it here.
              </Text>
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              {/* Search Filters */}
              <Box
                p={6}
                borderRadius="xl"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <Heading size="sm" mb={4}>
                  Search Requests
                </Heading>
                <VStack spacing={3} align="stretch">
                  <Flex gap={3} flexWrap="wrap">
                    {/* Address Search */}
                    <FormControl flex="1" minW="200px">
                      <FormLabel fontSize="xs" mb={1}>Address</FormLabel>
                      <Input
                        placeholder="Search by address..."
                        value={previousRequests.previousRequestsSearch.address}
                        onChange={(e) => previousRequests.updateSearchField('address', e.target.value)}
                        size="sm"
                      />
                    </FormControl>

                    {/* Service Search */}
                    <FormControl flex="1" minW="200px">
                      <FormLabel fontSize="xs" mb={1}>Service</FormLabel>
                      <Input
                        placeholder="Search by service..."
                        value={previousRequests.previousRequestsSearch.service}
                        onChange={(e) => previousRequests.updateSearchField('service', e.target.value)}
                        size="sm"
                      />
                    </FormControl>

                    {/* Date From */}
                    <FormControl flex="1" minW="150px">
                      <FormLabel fontSize="xs" mb={1}>From Date</FormLabel>
                      <Input
                        type="date"
                        value={previousRequests.previousRequestsSearch.dateFrom}
                        onChange={(e) => previousRequests.updateSearchField('dateFrom', e.target.value)}
                        size="sm"
                      />
                    </FormControl>

                    {/* Date To */}
                    <FormControl flex="1" minW="150px">
                      <FormLabel fontSize="xs" mb={1}>To Date</FormLabel>
                      <Input
                        type="date"
                        value={previousRequests.previousRequestsSearch.dateTo}
                        onChange={(e) => previousRequests.updateSearchField('dateTo', e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                  </Flex>

                  {/* Clear Filters Button */}
                  {previousRequests.hasActiveFilters && (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={previousRequests.clearSearchFilters}
                      alignSelf="flex-start"
                    >
                      Clear all filters
                    </Button>
                  )}

                  {/* Results Count */}
                  <Text fontSize="sm" color="gray.600">
                    {(() => {
                      const { filtered } = previousRequests.getDisplayedRequests();
                      return `Showing ${filtered.length} request${filtered.length !== 1 ? 's' : ''}`;
                    })()}
                  </Text>
                </VStack>
              </Box>

              {/* Request Cards */}
              {(() => {
                const { paginated, totalPages } = previousRequests.getDisplayedRequests();

                return (
                  <>
                    {paginated.length === 0 ? (
                      <Box
                        p={12}
                        borderRadius="xl"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="sm"
                        textAlign="center"
                      >
                        <Text fontSize="lg" color="gray.600" mb={2}>
                          No requests found
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Try adjusting your search filters
                        </Text>
                      </Box>
                    ) : (
                      <>
                        {paginated.map((request) => (
                <Box
                  key={request.id}
                  p={{ base: 4, md: 6 }}
                  borderRadius="xl"
                  bg="white"
                  border="1px solid"
                  borderColor={recommendations.bookingRequest?.id === request.id ? "blue.400" : "gray.200"}
                  boxShadow="sm"
                  cursor="pointer"
                  _hover={{ boxShadow: "md", borderColor: "blue.300" }}
                  transition="all 0.2s"
                  onClick={() => loadRequestDetails(request.id)}
                >
                  <Flex justify="space-between" align={{ base: "stretch", sm: "start" }} mb={3} direction={{ base: "column", sm: "row" }} gap={2}>
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={1}>
                        {request.address}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {new Date(request.created_at).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </Text>
                    </Box>
                    <Box
                      px={3}
                      py={1}
                      borderRadius="full"
                      bg="blue.100"
                      color="blue.700"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      {request.recommendations_count} recommendation{request.recommendations_count !== 1 ? 's' : ''}
                    </Box>
                  </Flex>

                  <Flex gap={4} flexWrap="wrap">
                    {request.product && (
                      <Box>
                        <Text fontSize="xs" color="gray.500">Service</Text>
                        <Text fontSize="sm" color="gray.700" fontWeight="medium">
                          {request.product.name}
                        </Text>
                      </Box>
                    )}
                    <Box>
                      <Text fontSize="xs" color="gray.500">Duration</Text>
                      <Text fontSize="sm" color="gray.700" fontWeight="medium">
                        {request.duration_minutes} min
                      </Text>
                    </Box>
                    {request.buffer_minutes > 0 && (
                      <Box>
                        <Text fontSize="xs" color="gray.500">Buffer</Text>
                        <Text fontSize="sm" color="gray.700" fontWeight="medium">
                          {request.buffer_minutes} min
                        </Text>
                      </Box>
                    )}
                  </Flex>
                </Box>
                        ))}

                        {/* Pagination Controls */}
                        <PaginationControls
                          currentPage={previousRequests.previousRequestsPage}
                          totalPages={totalPages}
                          onPageChange={previousRequests.setPreviousRequestsPage}
                        />
                      </>
                    )}
                  </>
                );
              })()}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
}
